import { useState } from 'react'
import { Check, X, Lightbulb, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { BobSuggestion } from '@/lib/services/bobAnalyseService'

type ItemDecisionState = 'pending' | 'accepted' | 'declined'

interface CategorySuggestionsProps {
  section: 'beeldvorming' | 'oordeelsvorming' | 'besluitvorming'
  category: string
  suggestions: BobSuggestion[]
  currentValues: string[]
  onAcceptCategory: (suggestionId: string, items: string[]) => Promise<void>
  onRejectCategory: (suggestionId: string) => Promise<void>
  disabled?: boolean
}

/**
 * Shows AI suggestions for a single category, displayed inline below the EditableBox.
 * Supports accepting/declining suggestions per individual line or per category as a whole.
 */
export function CategorySuggestions({
  section,
  category,
  suggestions,
  currentValues,
  onAcceptCategory,
  onRejectCategory,
  disabled = false,
}: CategorySuggestionsProps) {
  const [processingItems, setProcessingItems] = useState<Set<string>>(new Set())
  // Track per-item decisions: Record<suggestionId, Record<itemText, state>>
  const [itemDecisions, setItemDecisions] = useState<Record<string, Record<string, ItemDecisionState>>>({})

  // Get section key for accessing suggestion data
  const sectionKey = `${section}Suggestions` as 'beeldvormingSuggestions' | 'oordeelsvormingSuggestions' | 'besluitvormingSuggestions'

  // Filter to ready suggestions that have content for this specific category
  const relevantSuggestions = suggestions.filter(s => {
    if (s.status !== 'ready') return false
    const sectionSuggestions = s[sectionKey]
    return sectionSuggestions && category in sectionSuggestions && sectionSuggestions[category]?.length > 0
  })

  // Check for processing suggestions (show loading state)
  const processingSuggestions = suggestions.filter(
    s => s.status === 'pending' || s.status === 'processing'
  )
  const isProcessing = processingSuggestions.length > 0

  // Collect new items from all suggestions for this category
  const allNewItems: { suggestionId: string; items: string[] }[] = []
  const currentItemsSet = new Set(currentValues)

  relevantSuggestions.forEach(suggestion => {
    const sectionSuggestions = suggestion[sectionKey]
    if (!sectionSuggestions) return

    const items = sectionSuggestions[category]
    if (!items || items.length === 0) return

    // Filter out items that already exist
    const newItems = items.filter(item => !currentItemsSet.has(item))

    if (newItems.length > 0) {
      allNewItems.push({
        suggestionId: suggestion.id,
        items: newItems,
      })
    }
  })

  if (allNewItems.length === 0 && !isProcessing) {
    return null
  }

  const getItemState = (suggestionId: string, item: string): ItemDecisionState =>
    itemDecisions[suggestionId]?.[item] ?? 'pending'

  const finalizeGroup = async (suggestionId: string, groupDecisions: Record<string, ItemDecisionState>, allItems: string[]) => {
    const allDecided = allItems.every(i => groupDecisions[i] && groupDecisions[i] !== 'pending')
    if (!allDecided) return

    const acceptedItems = allItems.filter(i => groupDecisions[i] === 'accepted')
    setProcessingItems(prev => new Set(prev).add(suggestionId))
    try {
      if (acceptedItems.length > 0) {
        await onAcceptCategory(suggestionId, acceptedItems)
      } else {
        await onRejectCategory(suggestionId)
      }
    } finally {
      setProcessingItems(prev => {
        const next = new Set(prev)
        next.delete(suggestionId)
        return next
      })
    }
  }

  const handleItemDecision = async (
    suggestionId: string,
    item: string,
    decision: 'accepted' | 'declined',
    allItems: string[]
  ) => {
    const newGroupDecisions = {
      ...(itemDecisions[suggestionId] ?? {}),
      [item]: decision,
    }
    const newDecisions = { ...itemDecisions, [suggestionId]: newGroupDecisions }
    setItemDecisions(newDecisions)
    await finalizeGroup(suggestionId, newGroupDecisions, allItems)
  }

  const handleAcceptAll = async (suggestionId: string, items: string[]) => {
    const key = `${suggestionId}`
    setProcessingItems(prev => new Set(prev).add(key))
    try {
      await onAcceptCategory(suggestionId, items)
    } finally {
      setProcessingItems(prev => {
        const next = new Set(prev)
        next.delete(key)
        return next
      })
    }
  }

  const handleRejectAll = async (suggestionId: string) => {
    setProcessingItems(prev => new Set(prev).add(suggestionId))
    try {
      await onRejectCategory(suggestionId)
    } finally {
      setProcessingItems(prev => {
        const next = new Set(prev)
        next.delete(suggestionId)
        return next
      })
    }
  }

  return (
    <div className="mt-2 space-y-2">
      {/* Show loading state while generating - except for besluitvorming */}
      {isProcessing && section !== 'besluitvorming' && (
        <div className="flex items-center gap-2 px-3 py-2 bg-orange-50/50 rounded border border-orange-200">
          <Loader2 className="w-3 h-3 animate-spin text-orange-500" />
          <span className="text-xs text-orange-700">Suggesties worden gegenereerd...</span>
        </div>
      )}

      {/* Show suggestions */}
      {allNewItems.map(({ suggestionId, items }) => {
        const isProcessingThis = processingItems.has(suggestionId)
        const pendingItems = items.filter(item => getItemState(suggestionId, item) === 'pending')

        return (
          <div
            key={suggestionId}
            className="bg-orange-50/50 rounded border border-orange-200 overflow-hidden"
          >
            {/* Header: title + accept/decline all */}
            <div className="flex items-center justify-between gap-2 px-3 py-1.5 border-b border-orange-200">
              <div className="flex items-center gap-1.5">
                <Lightbulb className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />
                <span className="text-xs font-medium text-orange-700">
                  {pendingItems.length} suggestie{pendingItems.length !== 1 ? 's' : ''}
                </span>
              </div>
              {!disabled && (
                <div className="flex items-center gap-1">
                  {isProcessingThis ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-orange-500" />
                  ) : (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleAcceptAll(suggestionId, items)}
                        disabled={isProcessingThis}
                        className="h-6 px-2 gap-1 text-xs text-green-700 hover:text-green-800 hover:bg-green-100"
                        title="Alles accepteren"
                      >
                        <Check className="w-3 h-3" />
                        Accepteren
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRejectAll(suggestionId)}
                        disabled={isProcessingThis}
                        className="h-6 px-2 gap-1 text-xs text-red-600 hover:text-red-700 hover:bg-red-100"
                        title="Alles afwijzen"
                      >
                        <X className="w-3 h-3" />
                        Afwijzen
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Lines */}
            <ul className="px-3 py-2 space-y-1">
              {items.map((item, idx) => {
                const itemState = getItemState(suggestionId, item)
                return (
                  <li
                    key={idx}
                    className={cn(
                      "text-sm flex items-start gap-1",
                      itemState === 'pending' && "text-gray-700",
                      itemState === 'accepted' && "text-green-700",
                      itemState === 'declined' && "text-gray-400 line-through",
                    )}
                  >
                    <span
                      className={cn(
                        "mt-0.5 flex-shrink-0",
                        itemState === 'pending' && "text-orange-400",
                        itemState === 'accepted' && "text-green-400",
                        itemState === 'declined' && "text-gray-300",
                      )}
                    >
                      •
                    </span>
                    <span className="flex-1" dangerouslySetInnerHTML={{ __html: item }} />
                    {itemState === 'pending' && !isProcessingThis && !disabled && (
                      <div className="flex items-center gap-0.5 flex-shrink-0 ml-1">
                        <button
                          type="button"
                          onClick={() => handleItemDecision(suggestionId, item, 'accepted', items)}
                          className="h-5 w-5 rounded flex items-center justify-center text-green-600 hover:bg-green-100 transition-colors"
                          title="Accepteer deze regel"
                        >
                          <Check className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleItemDecision(suggestionId, item, 'declined', items)}
                          className="h-5 w-5 rounded flex items-center justify-center text-red-500 hover:bg-red-100 transition-colors"
                          title="Wijs deze regel af"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                    {itemState === 'accepted' && (
                      <Check className="w-3 h-3 text-green-500 flex-shrink-0 mt-0.5 ml-1" />
                    )}
                    {itemState === 'declined' && (
                      <X className="w-3 h-3 text-red-400 flex-shrink-0 mt-0.5 ml-1" />
                    )}
                  </li>
                )
              })}
            </ul>
          </div>
        )
      })}
    </div>
  )
}
